import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ArrowRight, Plane, MapPin, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { apiGet } from '../lib/api';

const ROUTES_VISIBLE_LIMIT = 15;

const PopularRoutes = () => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [routesList, setRoutesList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAllRoutes, setShowAllRoutes] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch popular routes
      const data = await apiGet('/api/popular-routes');
      if (data && data.success && data.routes) {
        setRoutes(data.routes);
      } else {
        setRoutes([
          {
            id: 1,
            name: 'Istanbul Airport to Taksim',
            pickup: 'Istanbul Airport (IST), Istanbul, Turkey',
            dropoff: 'Taksim, Istanbul, Turkey',
            price: 45,
            currency: 'EUR',
            duration: '45 min',
            isFeatured: true,
          },
          {
            id: 2,
            name: 'Antalya Airport to Belek',
            pickup: 'Antalya Airport (AYT), Antalya, Turkey',
            dropoff: 'Belek Hotels, Antalya, Turkey',
            price: 40,
            currency: 'EUR',
            duration: '30 min',
            isFeatured: true,
          },
          {
            id: 3,
            name: 'Antalya Airport to Side',
            pickup: 'Antalya Airport (AYT), Antalya, Turkey',
            dropoff: 'Side Hotels, Antalya, Turkey',
            price: 55,
            currency: 'EUR',
            duration: '50 min',
            isFeatured: false,
          },
        ]);
      }

      // Fetch routes list (quick links)
      const listData = await apiGet('/api/routes-list');
      if (listData && listData.success && listData.items) {
        setRoutesList(listData.items);
      }

      setIsLoading(false);
    };
    fetchData();
  }, []);

  const handleBookNow = (route) => {
    // Use full addresses for booking, short names for display
    const pickup = route.pickupFull || route.pickup;
    const dropoff = route.dropoffFull || route.dropoff;
    
    // Build URL params for prefill
    const params = new URLSearchParams({
      pickup: pickup,
      dropoff: dropoff,
      routeName: route.name || '',
    });
    
    if (route.pickupCoords) {
      params.set('pickupLat', route.pickupCoords.lat);
      params.set('pickupLng', route.pickupCoords.lng);
    }
    if (route.dropoffCoords) {
      params.set('dropoffLat', route.dropoffCoords.lat);
      params.set('dropoffLng', route.dropoffCoords.lng);
    }
    
    // First scroll to top immediately
    window.scrollTo(0, 0);
    
    // Navigate to home with prefill
    navigate(`/?${params.toString()}`);
    
    // Scroll to booking form after navigation
    setTimeout(() => {
      const bookingForm = document.getElementById('booking-form');
      if (bookingForm) {
        bookingForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
  };

  const formatPrice = (price, currency = 'EUR') => {
    if (!price) return '';
    return currency === 'EUR' ? `€${price}` : `${price} ${currency}`;
  };

  if (isLoading) {
    return (
      <section id="routes" className="py-16 md:py-24 bg-dark-elevated">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              Popular Routes
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Most popular airport transfers across Turkey
            </p>
          </div>
          <div className="text-center text-muted-foreground">
            Loading routes...
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="routes" className="py-16 md:py-24 bg-dark-elevated" data-testid="popular-routes-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            Popular Routes
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Most popular airport transfers across Turkey
          </p>
        </div>

        {/* Routes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {routes.slice(0, 6).map((route) => (
            <Card
              key={route.id}
              className="bg-secondary border-border route-card group"
              data-testid={`route-card-${route.id}`}
            >
              <CardContent className="p-6 flex flex-col h-full relative">
                {/* Popular Badge */}
                {route.isFeatured && (
                  <div className="absolute top-4 right-4">
                    <span className="text-xs font-semibold bg-primary text-primary-foreground px-2 py-1 rounded-full">
                      Popular
                    </span>
                  </div>
                )}

                {/* Route Info */}
                <div className="flex items-start gap-4 mb-6 flex-1">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Plane className="h-5 w-5 text-primary" />
                    </div>
                    <div className="w-px h-8 bg-border my-2"></div>
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="mb-4">
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">From</span>
                      <p className="font-semibold text-foreground">{route.pickup}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">To</span>
                      <p className="font-semibold text-foreground">{route.dropoff}</p>
                    </div>
                  </div>
                </div>

                {/* Fixed Price Microcopy */}
                <div className="mb-4">
                  <span className="text-xs text-primary font-medium">Fixed price · No hidden fees</span>
                </div>

                {/* Price & Duration */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div>
                    <span className="text-xs text-muted-foreground">Starting from</span>
                    <p className="font-display text-2xl font-bold text-primary">
                      {formatPrice(route.price, route.currency)}
                    </p>
                  </div>
                  {route.duration && (
                    <div className="text-right">
                      <span className="text-xs text-muted-foreground">Duration</span>
                      <p className="text-sm font-medium text-foreground">{route.duration}</p>
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <Button
                  variant="outline"
                  className="w-full mt-4 border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200"
                  onClick={() => handleBookNow(route)}
                  data-testid={`book-route-${route.id}`}
                >
                  Book Now
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Routes List — Quick Links */}
        {routesList.length > 0 && (
          <div className="mt-12 md:mt-16" data-testid="routes-list-section">
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-6 text-center">
              All Transfer Routes
            </h3>
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {(showAllRoutes ? routesList : routesList.slice(0, ROUTES_VISIBLE_LIMIT)).map((item) => (
                  <Link
                    key={item.id || item.url}
                    to={item.url}
                    className="group flex items-center justify-between px-4 py-3 rounded-lg
                      bg-secondary/50 hover:bg-primary/10 border border-transparent hover:border-primary/30
                      transition-all duration-200"
                    data-testid={`route-link-${item.id}`}
                  >
                    <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors truncate pr-2">
                      {item.name}
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary flex-shrink-0 transition-colors" />
                  </Link>
                ))}
              </div>
              {routesList.length > ROUTES_VISIBLE_LIMIT && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => setShowAllRoutes(!showAllRoutes)}
                    className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-medium
                      text-primary hover:text-primary/80 border border-primary/30 hover:border-primary/50
                      rounded-full transition-all duration-200"
                    data-testid="toggle-routes-list"
                  >
                    {showAllRoutes ? (
                      <>Show Less <ChevronUp className="h-4 w-4" /></>
                    ) : (
                      <>All Routes ({routesList.length}) <ChevronDown className="h-4 w-4" /></>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PopularRoutes;
